import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageSquare, ClipboardCheck, Users, Rocket } from 'lucide-react';

const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      icon: MessageSquare,
      number: '01',
      title: 'Free Consultation',
      description: 'We start with a no-obligation call to understand your business, challenges, and administrative needs.'
    },
    {
      icon: ClipboardCheck,
      number: '02',
      title: 'Tailored Proposal',
      description: 'We design a bespoke support package that fits your workflow, budget, and industry requirements.'
    },
    {
      icon: Users,
      number: '03',
      title: 'Meet Your Team',
      description: 'Get matched with a dedicated administrative expert who integrates seamlessly with your existing tools.'
    },
    {
      icon: Rocket,
      number: '04',
      title: 'Scale & Grow',
      description: 'Focus on what matters while we handle the rest. Adjust your support as your business evolves.'
    }
  ];

  return (
    <section id="how-it-works" ref={ref} className="relative py-32 bg-slate-50 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-100 to-blue-100 text-yellow-700 rounded-full text-sm font-semibold mb-6">
            HOW IT WORKS
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Simple. Seamless.
            <span className="block bg-gradient-to-r from-yellow-600 via-blue-600 to-yellow-600 bg-clip-text text-transparent leading-tight pb-2">Stress-Free.</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Getting started with Flexdesk UK is effortless. Four simple steps to transform your operations.
          </p>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-300 via-blue-300 to-yellow-300" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative group"
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-slate-100 hover:border-yellow-400/40 h-full">
                    <div className="relative mb-6 inline-block">
                      <div className="w-16 h-16 bg-gradient-to-br from-slate-900 to-blue-950 rounded-2xl flex items-center justify-center shadow-lg group-hover:from-yellow-500 group-hover:to-yellow-600 transition-all duration-500">
                        <Icon className="w-8 h-8 text-yellow-400 group-hover:text-slate-900 transition-colors duration-500" />
                      </div>
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center text-xs font-bold text-slate-900 shadow-lg">
                        {index + 1}
                      </div>
                    </div>

                    <div className="text-6xl font-bold text-slate-100 absolute top-6 right-6 group-hover:text-yellow-100 transition-colors duration-500">
                      {step.number}
                    </div>

                    <h3 className="text-2xl font-bold text-slate-900 mb-3 relative z-10">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed relative z-10">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="how-it-works-cta"
            className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-slate-900 font-bold rounded-xl transition-all duration-300 shadow-lg shadow-yellow-500/40 hover:scale-105"
          >
            Book Your Free Consultation
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
